/**
 * ColorSpecs — renders a unique, non-templated "Colour Specifications &
 * Pairings" section for every colour detail page. Every value shown here
 * (hex, RGB, HSL, CMYK, pairing matches) is mathematically derived from that
 * colour's specific hex code, making each page's content genuinely distinct.
 */
import { Link } from "wouter";
import {
  hexToRgb,
  hexToHsl,
  hexToCmyk,
  hueLabel,
  findPairings,
} from "@/lib/color-specs";
import { COLOR_LIST } from "@/data/colors";

interface ColorSpecsProps {
  hex: string;
  id: string;
  name: string;
}

export function ColorSpecs({ hex, id, name }: ColorSpecsProps) {
  const rgb  = hexToRgb(hex);
  const hsl  = hexToHsl(hex);
  const cmyk = hexToCmyk(hex);
  const pairings = findPairings(hex, id, COLOR_LIST);

  const specRows = [
    { label: "HEX",  value: hex.toUpperCase() },
    { label: "RGB",  value: `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})` },
    { label: "HSL",  value: `hsl(${hsl.h}°, ${hsl.s}%, ${hsl.l}%)` },
    { label: "CMYK", value: `C ${cmyk.c}%  M ${cmyk.m}%  Y ${cmyk.y}%  K ${cmyk.k}%` },
    { label: "Hue",  value: hueLabel(hsl.h) },
    {
      label: "Tone",
      value: hsl.l >= 80 ? "Light" : hsl.l <= 30 ? "Dark" : "Mid-tone",
    },
    {
      label: "Saturation",
      value: hsl.s >= 70 ? "High — vivid, saturated" : hsl.s >= 35 ? "Medium — muted, natural" : "Low — near-neutral, desaturated",
    },
  ];

  return (
    <section aria-label={`${name} colour specifications and pairings`} className="mt-14 space-y-10">
      {/* ── Specifications ── */}
      <div>
        <h2 className="text-lg font-serif mb-4">{name} — colour specifications</h2>
        <p className="text-sm text-muted-foreground leading-relaxed mb-6">
          {hex.toUpperCase()} is a {
            hsl.l >= 80 ? "light" : hsl.l <= 30 ? "dark" : "mid-tone"
          }, {
            hsl.s >= 70 ? "highly saturated" : hsl.s >= 35 ? "moderately saturated" : "low-saturation"
          } {hueLabel(hsl.h).split(" at")[0]} tone with a hue angle of {hsl.h}°,{" "}
          {hsl.s}% saturation, and {hsl.l}% lightness in the HSL colour model.
          In RGB it breaks down to red {rgb.r}, green {rgb.g}, blue {rgb.b}.
          In print (CMYK) it requires {cmyk.c}% cyan, {cmyk.m}% magenta, {cmyk.y}% yellow,
          and {cmyk.k}% black.
        </p>

        <div className="rounded-2xl border border-border overflow-hidden">
          <table className="w-full text-sm">
            <tbody>
              {specRows.map((row, i) => (
                <tr key={row.label} className={i % 2 === 0 ? "bg-muted/40" : ""}>
                  <td className="py-2.5 px-4 font-medium text-muted-foreground w-28 shrink-0">
                    {row.label}
                  </td>
                  <td className="py-2.5 px-4 font-mono text-xs tracking-wide">
                    {row.label === "HEX" ? (
                      <span className="flex items-center gap-2">
                        <span
                          className="inline-block w-4 h-4 rounded-sm border border-black/10"
                          style={{ backgroundColor: hex }}
                        />
                        {row.value}
                      </span>
                    ) : row.value}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Colour pairings ── */}
      {pairings.length > 0 && (
        <div>
          <h2 className="text-lg font-serif mb-2">Colours that pair with {name}</h2>
          <p className="text-sm text-muted-foreground leading-relaxed mb-5">
            These pairings are calculated from {name}&apos;s position on the colour wheel
            (hue {hsl.h}°) and matched to the closest colours in the library. Use them
            as a starting point for palettes, branding, or interior schemes.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {pairings.map((p) => (
              <Link
                key={p.id}
                href={`/learn/${p.id}`}
                className="group flex flex-col gap-2 focus:outline-none"
                aria-label={`${p.role}: ${p.name}`}
              >
                <span
                  className="block w-full aspect-square rounded-xl border border-black/5 shadow-sm transition-transform duration-300 group-hover:scale-105 group-focus-visible:ring-2 group-focus-visible:ring-primary"
                  style={{ backgroundColor: p.hex }}
                />
                <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
                  {p.role}
                </span>
                <span className="text-sm font-serif leading-snug group-hover:text-primary transition-colors">
                  {p.name}
                </span>
                <span className="text-xs text-muted-foreground leading-relaxed">
                  {p.description}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
